import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import toast from "../helpers/toast";
import { logout } from "../components/pages/auth/authSlice.js";
import { getApiBaseUrl } from "../helpers/apiUrl";

const getNormalizedErrorMessage = (error) => {
  if (!error) return "Something went wrong";

  const genericBackendMessages = new Set([
    "Exception",
    "ValueError",
    "TypeError",
    "AttributeError",
    "RuntimeError",
    "Conflict",
  ]);

  let data = error?.data;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      data = { message: data };
    }
  }

  const message = data?.message;
  const verbose = data?.verbose;

  if (message && !genericBackendMessages.has(message)) {
    return message;
  }

  return verbose || message || error?.error || error?.message || "Something went wrong";
};

const parentQueryEndpoints = new Set([
  "getParentStudents",
  "getStudentInvoices",
  "getStudentReports",
  "getStudent",
  "getMyStudentProfile",
  "getSchoolBankAccount",
  "getStudentQuizHistory",
  "getStudentAnalytics",
  "getParentAiBalance",
  "getParentAiHistory",
  "getParentAiUsage",
]);

const getQueryErrorToastMessage = (api, error) => {
  if (error?.status === 404 && parentQueryEndpoints.has(api?.endpoint)) {
    return null;
  }
  return error?.message || "Something went wrong";
};

const baseQuery = fetchBaseQuery({
  baseUrl: getApiBaseUrl(),
  //credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const access_token = getState().auth.access_token;
    if (access_token) {
      headers.set("authorization", `Bearer ${access_token}`);
    }
    return headers;
  },
});

const baseQuerywithAuth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  if (result?.error) {
    result.error.message = getNormalizedErrorMessage(result.error);

    // For queries (not mutations), auto-show the error — the component only
    // destructures `data` so it will never show anything on failure.
    // Mutations handle their own errors via unwrap() + catch.
    if (api.type === 'query' && result.error.status !== 401) {
      const toastMessage = getQueryErrorToastMessage(api, result.error);
      if (toastMessage) {
        toast.error(toastMessage);
      }
    }
  }
  const isLoginRequest = args?.url === "/auth/login";
  if (result?.error?.status === 401 && !isLoginRequest) {
    // unauthorized / expired session (not a login attempt)
    toast.warning("Your session has expired. Please login again.");
    api.dispatch(logout()); // logout user
    api.dispatch(authApiSlice.util.resetApiState());
  }

  return result; //if everything is ok, return the result
};

export const authApiSlice = createApi({
  reducerPath: "authApi",
  baseQuery: baseQuerywithAuth,
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (values) => ({
        url: "/auth/login",
        method: "POST",
        body: { ...values },
      }),
      invalidatesTags: ["User"],
    }),

    getAllUsers: builder.query({
      query: ({
        school_id,
        includes = [],
        excludes = [],
        page = 1,
        per_page = 20,
        q = "",
      }) => {
        const includeParams = includes
          .map((role) => `include=${role}`)
          .join("&");
        const excludeParams = excludes
          .map((role) => `exclude=${role}`)
          .join("&");
        const qParam = q ? `&q=${encodeURIComponent(q)}` : "";

        return `/auth/users/${school_id}?${includeParams}${
          excludeParams ? `&${excludeParams}` : ""
        }${qParam}&page=${page}&per_page=${per_page}`;
      },
      providesTags: ["Users"],
    }),

    createUser: builder.mutation({
      query: (values) => ({
        url: "/auth/add-user",
        method: "POST",
        body: { ...values },
      }),
    }),

    updatePassword: builder.mutation({
      query: (values) => ({
        url: "/auth/update-password",
        method: "PATCH",
        body: { ...values },
      }),
      invalidatesTags: ["User"],
    }),

    setInitialPassword: builder.mutation({
      query: (values) => ({
        url: "/auth/set-initial-password",
        method: "PATCH",
        body: { ...values },
      }),
    }),

    forgotPassword: builder.mutation({
      query: (values) => ({
        url: "/auth/forgot-password",
        method: "POST",
        body: { ...values },
      }),
    }),

    resetPassword: builder.mutation({
      query: (values) => ({
        url: "/auth/reset-password",
        method: "POST",
        body: { ...values },
      }),
    }),

    adminResetPassword: builder.mutation({
      query: (userId) => ({
        url: `/auth/admin-reset-password/${userId}`,
        method: "PATCH",
      }),
      invalidatesTags: ["Users"],
    }),

    toggleUserStatus: builder.mutation({
      query: (userId) => ({
        url: `/auth/users/${userId}/toggle-status`,
        method: "PATCH",
      }),
      invalidatesTags: ["Users"],
    }),

    getPublicSchoolInfo: builder.query({
      query: () => `/school/info`,
    }),
  }),
});

export const {
  useLoginMutation,
  useCreateUserMutation,
  useUpdatePasswordMutation,
  useSetInitialPasswordMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useAdminResetPasswordMutation,
  useToggleUserStatusMutation,
  useGetAllUsersQuery,
  useGetPublicSchoolInfoQuery,
} = authApiSlice;
